import { useState } from "react";
import { motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { IMAGES } from "@/lib/images";
import { PageHero, SectionTitle } from "@/components/Shared";
import Lightbox from "@/components/Lightbox";
import ShareButton from "@/components/ShareButton";
import Reviews from "@/components/Reviews";
import { Flame, Droplets, Waves, Hand, Leaf, Coffee } from "lucide-react";

const SERVIZI = [
  { icon: Flame, title: "Sauna Finlandese", desc: "Calore secco a temperatura elevata per depurare corpo e mente." },
  { icon: Droplets, title: "Bagno Turco", desc: "Vapore avvolgente per un profondo effetto rilassante e purificante." },
  { icon: Waves, title: "Idromassaggio Emozionale", desc: "Getti d'acqua e cromoterapia per un totale abbandono ai sensi." },
  { icon: Hand, title: "Massaggi Relax", desc: "Trattamenti manuali eseguiti in un ambiente caldo e discreto." },
  { icon: Leaf, title: "Rituali Naturisti", desc: "Esperienze di benessere naturista nel rispetto della libertà personale." },
  { icon: Coffee, title: "Area Relax & Tisane", desc: "Uno spazio caldo dove concedersi una pausa tra tisane e silenzio." },
];

export default function SpaNaturista() {
  const { settings, events } = useSite();
  const spaEvents = events.filter((e) => e.category === "spa");
  const stripImgs = [IMAGES.spa_candles, IMAGES.spa_amb, IMAGES.spa_entrance];
  const spaItems = [
    ...spaEvents.map((e) => ({ id: e.id, image_url: e.image_url || IMAGES.spa_amb, caption: e.title })),
    ...stripImgs.map((img, j) => ({ id: `strip-${j}`, image_url: img, caption: "" })),
  ];
  const [lbIndex, setLbIndex] = useState(null);
  return (
    <div>
      <PageHero
        overline="Il Benessere di Afrodite"
        title="Spa Naturista"
        subtitle={settings.spa_intro}
        image={IMAGES.hero_spa}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <SectionTitle overline="Gli Appuntamenti" title="I Pomeriggi Naturisti" />
        <p className="text-center text-sm text-[#A39E9B] max-w-xl mx-auto -mt-8 mb-12">
          Aperture pomeridiane ogni <span className="text-[#C59B6D]">Martedì, Giovedì e Domenica</span>.
        </p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-sm sm:max-w-none mx-auto">
          {spaEvents.map((ev, i) => (
            <motion.article
              key={ev.id}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.6 }}
              className="group relative rounded-2xl overflow-hidden border border-[#C59B6D]/15 bg-[#141217]"
            >
              <div onClick={() => setLbIndex(i)} data-testid={`spa-event-img-${ev.id}`} className="relative aspect-[9/16] overflow-hidden cursor-pointer bg-black">
                <img src={ev.image_url || IMAGES.spa_amb} alt={ev.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(180deg, transparent, rgba(20,18,23,0.95))" }} />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#C59B6D] text-[#1a130c] text-xs font-semibold tracking-wide">
                  {ev.day}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl text-[#FBF8F5] mb-2">{ev.title}</h3>
                {ev.theme && <p className="text-sm text-[#C59B6D] mb-3">{ev.theme}</p>}
                <p className="text-sm text-[#A39E9B] leading-relaxed mb-3">{ev.info}</p>
                <div className="flex items-center gap-3">
                  <ShareButton className="ml-auto" testid={`spa-share-${ev.id}`} title={ev.title} when={`Ogni ${ev.day}`} image={ev.image_url} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-4">
        <SectionTitle overline="I Servizi" title="Benessere & Relax" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVIZI.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="p-7 rounded-2xl border border-[#C59B6D]/15 bg-[#141217] hover:border-[#C59B6D]/40 transition-colors duration-500"
            >
              <div className="w-12 h-12 rounded-full border border-[#C59B6D]/40 flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-[#C59B6D]" />
              </div>
              <h3 className="font-display text-xl text-[#FBF8F5] mb-2">{s.title}</h3>
              <p className="text-sm text-[#A39E9B] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid gap-4 sm:grid-cols-3">
          {stripImgs.map((img, j) => (
            <motion.div key={j}
              onClick={() => setLbIndex(spaEvents.length + j)}
              initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ delay: j * 0.08 }}
              className="relative h-72 rounded-2xl overflow-hidden group cursor-pointer">
              <img src={img} alt="Spa Afrodite" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 vignette opacity-70" />
            </motion.div>
          ))}
        </div>
      </section>

      <Reviews category="spa" />

      <Lightbox items={spaItems} index={lbIndex} setIndex={setLbIndex} />
    </div>
  );
}
